import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	normalizeSocialListeningPlatform,
	SocialListeningMention,
	SocialListeningMentionApi,
	SocialListeningMentionsRequestBody,
	SocialListeningMentionsResponse,
	SocialListeningMentionsResponseApi
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-mentions.model';
import { SocialListeningSentimentType } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/filters/sentiment/sentiment-chip.component';
import { smiUrl } from 'components';
import { catchError, map, Observable, of } from 'rxjs';

const EMPTY_RESPONSE: SocialListeningMentionsResponseApi = {
	last_update_date: '',
	mentions: [],
	pagination: {
		page_number: 1,
		page_size: 20,
		total_pages: 1,
		records: 0
	}
};

@Injectable({
	providedIn: 'root'
})
export class GetSocialListeningMyBrandMentionsService {
	constructor(private http: HttpClient) {}

	fetch(
		brandId: string,
		body: SocialListeningMentionsRequestBody = {}
	): Observable<SocialListeningMentionsResponse> {
		const url = smiUrl()
			.add('social-listening')
			.add('brand')
			.add(brandId)
			.add('mentions')
			.add('list')
			.done();

		return this.http
			.post<SocialListeningMentionsResponseApi>(url, body)
			.pipe(
				// An empty page, as the competitor feed does. This used to answer
				// a failed read with five invented mentions ("Alex Johnson" ...),
				// so an outage looked like a feed.
				catchError(() => of(EMPTY_RESPONSE)),
				map(response => this.mapResponse(response))
			);
	}

	private mapMention(m: SocialListeningMentionApi): SocialListeningMention {
		return {
			id: m.id ?? '',
			// Forward the resolved PG influencer id (when set) so the
			// author-detail slider can open with a real profile instead
			// of falling back to a hardcoded ID that 404s on UAT/prod.
			influencerId: m.influencer_id ?? undefined,
			authorName: m.author_name,
			authorUsername: m.author_username,
			authorImage: m.author_image,
			authorVerified: m.author_verified,
			authorUrl: m.author_url,
			authorPlatform: normalizeSocialListeningPlatform(m.author_platform),
			authorPlatformId: m.author_platform_id ?? undefined,
			postUrl: m.post_url ?? m.author_url ?? '',
			postCaption: m.post_caption,
			postImage: m.post_image,
			postLikes: m.post_likes,
			postComments: m.post_comments,
			postViews: m.post_views,
			postEngagementRate: m.post_engagement_rate,
			postEstimatedReach: m.post_estimated_reach,
			postDate: m.post_date,
			sentiment: m.sentiment as SocialListeningSentimentType,
			emotion: m.emotion as any,
			language: m.language,
			tags: m.tags ?? [],
			matchedKeywords: m.matched_keywords ?? [],
			title: m.title ?? '',
			domainVisits: m.domain_visits ?? 0,
			isMessageable: m.is_messageable ?? false,
			origin: m.origin ?? 'earned',
			parentUrl: m.parent_url ?? '',
			ownAccount: m.own_account ?? ''
		};
	}

	private mapResponse(
		response: SocialListeningMentionsResponseApi
	): SocialListeningMentionsResponse {
		return {
			lastUpdateDate: response.last_update_date,
			mentions: response.mentions.map(m => this.mapMention(m)),
			pagination: {
				page_number: response.pagination.page_number,
				total_pages: response.pagination.total_pages,
				records: response.pagination.records
			},
			mentionsLocked: response.mentions_locked === true
		};
	}
}
